"use client";

import { motion } from "motion/react";
import { useState, ReactNode } from "react";
import { ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";

type Entry = { id: string; label: string; body: string; icon?: ReactNode; accent?: "red" | "coral" | "purple" | "cyan" | "denim" | "emerald" | "lime" | "amber-gold" | "crimson"; children?: Entry[] };

const ACCENT_TEXT: Record<NonNullable<Entry["accent"]>, string> = {
  red: "text-red-400", coral: "text-coral-400", purple: "text-purple-400", cyan: "text-cyan-400", denim: "text-denim-400", emerald: "text-emerald-400", lime: "text-lime-400", "amber-gold": "text-amber-gold-400", crimson: "text-crimson-400",
};

function Node({ entry, level = 0 }: { entry: Entry; level?: number }) {
  const [open, setOpen] = useState(level < 2);
  const hasChildren = entry.children && entry.children.length > 0;
  const accent = entry.accent ?? "purple";

  return (
    <div className={cn("relative", level > 0 && "ml-6 border-l border-border/50 pl-4")}>
      <motion.div initial={{ opacity: 0, x: -10 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.3 }} className="mb-2">
        <button onClick={() => setOpen((o) => !o)} className={cn("group flex w-full items-center gap-3 rounded-xl border border-border p-3 text-left transition-colors hover:border-amber-gold-500/40", open && "border-amber-gold-500/40 bg-muted/50")}>
          <div className={cn("flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-card", ACCENT_TEXT[accent])}>{entry.icon ?? <span className="font-rajdhani text-sm font-bold">{level + 1}</span>}</div>
          <div className="flex-1">
            <div className="font-rajdhani text-sm font-semibold text-foreground">{entry.label}</div>
            {open && <p className="mt-1 text-xs leading-relaxed text-foreground/80/80">{entry.body}</p>}
          </div>
          {hasChildren && <ChevronRight className={cn("h-4 w-4 shrink-0 text-muted-foreground transition-transform", open && "rotate-90 text-amber-gold-500")} />}
        </button>
      </motion.div>
      {hasChildren && open && <div className="mt-2 space-y-2">{entry.children!.map((child, i) => <Node key={i} entry={child} level={level + 1} />)}</div>}
    </div>
  );
}

export function TreeBlock({ entries, title, eyebrow, description }: { entries: Entry[]; title?: string; eyebrow?: string; description?: string }) {
  return (
    <div className="space-y-8">
      {(eyebrow || title || description) && (
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mx-auto max-w-3xl text-center">
          {eyebrow && <div className="mb-3 text-xs font-bold uppercase tracking-[0.3em] text-amber-gold-500">{eyebrow}</div>}
          {title && <h2 className="font-rajdhani text-3xl font-bold text-foreground lg:text-4xl">{title}</h2>}
          {description && <p className="mt-3 text-foreground/80/80">{description}</p>}
        </motion.div>
      )}
      <div className="glass-card rounded-2xl border border-border p-6">
        <div className="space-y-2">{entries.map((e, i) => <Node key={i} entry={e} />)}</div>
      </div>
    </div>
  );
}
